import { AccountTypes } from '@interfaces/models';
import { createSelector } from '@ngrx/store';
import { getFormattedBrokerageAccounts } from '@shared/helpers';
import { RoleTypes } from '@shared/models';
import {
  selectEligibleInternalTransferFromAccounts,
  selectEligibleInternalTransferToAccounts,
} from '@store/selectors/client-accounts/client-accounts.selectors';
import {
  selectInternalTransferFromAccountType,
  selectInternalTransferIsOwner,
  selectInternalTransferToAccountType,
} from '@store/selectors/internal-transfer/internal-transfer.selectors';

export const selectInternalTransferEligibility = createSelector(
  selectEligibleInternalTransferFromAccounts,
  selectEligibleInternalTransferToAccounts,
  selectInternalTransferFromAccountType,
  selectInternalTransferToAccountType,
  selectInternalTransferIsOwner,
  (
    eligibleInternalTransferFromAccounts,
    eligibleInternalTransferToAccounts,
    accountFromType,
    accountToType,
    isOwner,
  ) => {
    const hasEligibleFromAccounts: boolean =
      getFormattedBrokerageAccounts(eligibleInternalTransferFromAccounts.accounts).length >= 1;

    const canOpenNewAccount: boolean =
      (accountFromType === AccountTypes.INDV || accountFromType === AccountTypes.ESTATE) &&
      accountToType === AccountTypes.INDV;
    const isSameAccountTypesForFromAndTo: boolean = accountFromType === accountToType;

    let hasEligibleToAccounts: boolean = false;

    if (!isOwner || canOpenNewAccount) {
      hasEligibleToAccounts = true;
    } else {
      const formattedAccountsLength: number = getFormattedBrokerageAccounts(
        eligibleInternalTransferToAccounts.accounts,
      ).length;
      hasEligibleToAccounts = isSameAccountTypesForFromAndTo
        ? formattedAccountsLength >= 2
        : formattedAccountsLength >= 1;
    }

    // Determine eligibility
    const isInternalTransferEligible: boolean = hasEligibleFromAccounts && hasEligibleToAccounts;

    switch (true) {
      case isInternalTransferEligible:
        return {
          isInternalTransferEligible: true,
          fromAccounts: eligibleInternalTransferFromAccounts.accounts,
          accountFromType,
        };
      case !hasEligibleToAccounts && !hasEligibleFromAccounts:
        return {
          isInternalTransferEligible: false,
          ineligibleToAccountType: accountToType,
          ineligibleFromAccountType: accountFromType,
        };
      case !hasEligibleToAccounts:
        return {
          isInternalTransferEligible: false,
          ineligibleToAccountType: accountToType,
        };
      case !hasEligibleFromAccounts:
        return {
          isInternalTransferEligible: false,
          ineligibleFromAccountType: accountFromType,
        };
      /* istanbul ignore next */
      default:
        throw new Error('UNREACHABLE');
    }
  },
);
